<script setup lang="ts">
import { close } from '@/components/icons/icons.ts';
import { computed, onMounted, onUnmounted } from 'vue';
import type { DialogProps, DialogSize } from './types';
import Tooltip from '@/components/Tooltip/Tooltip.vue';
import Button from '@/components/buttons/Button.vue';
const modelValue = defineModel<boolean>({ type: Boolean, default: false });

const props = withDefaults(defineProps<DialogProps>(), {
  persistent: true,
  scrollable: true,
});

const width = computed(() => {
  const sizeTable: Record<DialogSize, string | number> = {
    small: 320,
    medium: 520,
    large: 960,
    'x-large': 1200,
  };
  return props.size ? sizeTable[props.size] : 'auto';
});

const passdownProps = computed(() => ({
  ...props,
  size: undefined,
  hideClose: undefined,
  color: undefined,
}));

function onKeydown(e: KeyboardEvent) {
  if (e.key === 'Escape') {
    onClose();
  }
}

const borderClasses = computed(() =>
  props.notificationType
    ? ['border-md', 'border-opacity-100', `border-${props.notificationType}`]
    : [],
);

onMounted(() => {
  document.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown);
});

function onClose() {
  if (!props.hideClose) {
    modelValue.value = false;
  }
}
</script>

<template>
  <v-dialog
    :modelValue="modelValue"
    :width="width"
    v-bind="passdownProps"
    @update:modelValue="(value) => (modelValue = value)"
  >
    <v-card elevation="16" :color="color" :class="borderClasses">
      <v-card-item class="cardItem position-relative">
        <Tooltip v-if="!hideClose" position="left">
          <template #tooltip>
            {{ $t('Close') }}
          </template>
          <template #target="{ props: tooltipProps }">
            <Button
              v-bind="tooltipProps"
              variant="text"
              :icon="close"
              size="small"
              class="close"
              @click.stop="onClose"
            />
          </template>
        </Tooltip>

        <v-card-title
          :class="['cardTitle', 'text-h3', 'text-primary-darken-2']"
          style="margin-top: 16px"
        >
          <slot name="title" />
        </v-card-title>
      </v-card-item>

      <v-card-text class="text-body-1 text-primary">
        <slot name="text" />
      </v-card-text>

      <v-card-actions v-if="$slots.actions" class="cardActions">
        <slot name="actions" />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.cardTitle {
  display: flex;
  align-items: center;
  white-space: normal;
  word-break: keep-all;
  hyphens: manual;
  padding-bottom: 16px;
}

.cardActions {
  width: 100%;
  display: flex;
  gap: 8px;
  justify-content: end;
  padding: 16px 32px 16px 32px;
}

.close {
  position: absolute;
  top: 8px;
  right: 8px;
}
</style>
